import * as React from 'react'
import { createPortal } from 'react-dom'
import { UseFormGetValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ModalWrapper } from '~/atoms/modal/ModalWrapper'
import { SearchType } from '~/organisms/search/SearchType'
import {
  SearchCondition,
  makeSearchConditionSummaryText,
} from '~/organisms/search/SearchCondition'
import { SearchDate } from '~/organisms/search/SearchDate'
import { SearchTime } from '~/organisms/search/SearchTime'

// images
import SearchIcon from './search.svg?reactCompornt'
import CircleArrowRightIcon from './circle-arrow-right.svg?reactCompornt'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarCheck } from '@fortawesome/free-regular-svg-icons'

import {
  faChevronRight,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons'

import * as Style from './style.css'

export interface IProps {
  pageType?: string
  todaySearchUrl: string
  tomorrowSearchUrl: string
  modalType: string
  prefectures: [string, string][]
  searchState: ISearchState
  enableHousekeepingSubsidy?: boolean
  setModal: (modalType: string) => void
  getValues: UseFormGetValues<ISearchState>
  setSearchTypeHandler: (searchType: ISearchType) => void
  searchConditionSubmitHandler: (state: ISearchConditionState) => void
  searchDateSubmitHandler: (state: ISearchDateState) => void
  searchTimeSubmitHandler: (state: ISearchTimeState) => void
  clickTodayLinkHandler: () => void
  clickTomorrowLinkHandler: () => void
  submitHandler: () => void
}

export const SearchForm = ({
  pageType,
  todaySearchUrl,
  tomorrowSearchUrl,
  modalType,
  prefectures,
  searchState,
  enableHousekeepingSubsidy,
  setModal,
  getValues,
  setSearchTypeHandler,
  searchConditionSubmitHandler,
  searchDateSubmitHandler,
  searchTimeSubmitHandler,
  clickTodayLinkHandler,
  clickTomorrowLinkHandler,
  submitHandler,
}: IProps): JSX.Element => {
  const { t } = useTranslation()
  const minDate = new Date()
  const maxDate = new Date(minDate.getFullYear() + 2, 11, 31)
  const [isInitialized, setInitialized] = React.useState(false)
  React.useEffect(() => {
    setInitialized(true)
  }, [true])

  return (
    <div className={Style.SearchForm}>
      {pageType === 'regularLpBottom' && (
        <div
          className={Style.SearchMainText}
          onClick={() => setModal('filter')}
        >
          <FontAwesomeIcon icon={faCalendarCheck} size="1x" />
          <span className={Style.IconText}>まずは単発予約</span>
          <FontAwesomeIcon icon={faChevronRight} size="xs" />
        </div>
      )}
      {pageType === 'regularLpBottomNormal' && (
        <div
          className={Style.SearchMainText}
          onClick={() => setModal('filter')}
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} size="1x" />
          <span className={Style.IconText}>検索で探す</span>
          <FontAwesomeIcon icon={faChevronRight} size="xs" />
        </div>
      )}
      {pageType === 'search' && (
        <span
          className={Style.SearchResultLabel}
          onClick={() => setModal('filter')}
        >
          定期利用を探す
        </span>
      )}
      {pageType === 'top' && (
        <>
          <div className={Style.SearchFormTop}>
            <p className={Style.SearchFormPurpose}>
              ご利用目的をお選びください
            </p>
            <div className={Style.SearchFormType}>
              <SearchType
                setSearchTypeHandler={setSearchTypeHandler}
                getValues={getValues}
              />
            </div>
            <div className={Style.SearchFormSummaries}>
              <div className={Style.SearchFormSummaryWrapper}>
                <div
                  className={Style.SearchFormSummaryCondition}
                  onClick={() => setModal('filter')}
                >
                  <SearchIcon
                    className={Style.SearchFormSummaryConditionIcon}
                  />
                  <span className={Style.SearchFormSummaryTextActive}>
                    {makeSearchConditionSummaryText(
                      searchState.search,
                      searchState.regular_search,
                      prefectures,
                      searchState.search.geocode,
                      searchState.search.station,
                      searchState.reserveType,
                      t,
                    )}
                  </span>
                  <CircleArrowRightIcon
                    className={Style.SearchFormSummaryCircleArrowRightIconIcon}
                  />
                </div>
              </div>
              <button
                className={Style.SearchFormSubmit}
                onClick={() => setModal('filter')}
              >
                条件を絞り込む
              </button>
            </div>
          </div>
        </>
      )}
      {pageType === 'regularLp' && (
        <>
          <div
            className={Style.SearchFormRegularButton}
            onClick={() => setModal('filter')}
          >
            定期で検索する
          </div>
        </>
      )}
      {pageType === 'leftNavi' && (
        <>
          <span
            onClick={() => setModal('filter')}
            className={Style.LeftNaviIconText}
          >
            単発利用を探す
            <FontAwesomeIcon icon={faChevronRight} size="xs" />
          </span>
        </>
      )}
      {isInitialized &&
        createPortal(
          <>
            <ModalWrapper
              mobileAnimation="slideUp"
              isOpen={modalType === 'filter'}
            >
              <div className={Style.SearchFormModalLarge}>
                <SearchCondition
                  prefectures={prefectures}
                  enableHousekeepingSubsidy={enableHousekeepingSubsidy}
                  getValues={getValues}
                  submitHandler={searchConditionSubmitHandler}
                  closeHandler={() => setModal('')}
                  submitLabel="検索"
                />
              </div>
            </ModalWrapper>
            <ModalWrapper mobileAnimation="fadeIn" isOpen={modalType === 'day'}>
              <div className={Style.SearchFormModalSmall}>
                <SearchDate
                  getValues={getValues}
                  submitHandler={searchDateSubmitHandler}
                  closeHandler={() => setModal('')}
                  submitLabel="決定"
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </div>
            </ModalWrapper>
            <ModalWrapper
              mobileAnimation="fadeIn"
              isOpen={modalType === 'time'}
            >
              <div className={Style.SearchFormModalSmall}>
                <SearchTime
                  submitLabel="決定"
                  getValues={getValues}
                  submitHandler={searchTimeSubmitHandler}
                  closeHandler={() => setModal('')}
                />
              </div>
            </ModalWrapper>
          </>,
          document.body,
        )}
      {pageType === 'top' && (
        <div className={Style.SearchFormBottom}>
          <div className={Style.SearchFormLinks}>
            <a
              className={Style.SearchFormLink}
              onClick={clickTodayLinkHandler}
              href={todaySearchUrl}
            >
              今日きてくれる
            </a>
            <a
              className={Style.SearchFormLink}
              onClick={clickTomorrowLinkHandler}
              href={tomorrowSearchUrl}
            >
              明日きてくれる
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
